import { Stack, styled } from '@mui/material';

import { scrollbarStyles, theme } from '@/shared/design-system';
import { forMobile } from '@/styles/responsive';

export const PageRoot = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'column',
  gap: '24px',
  width: '100%',

  [forMobile(muiTheme)]: {
    gap: '16px',
  },
}));

export const PageHeader = styled(Stack)({
  flexDirection: 'column',
  gap: '4px',
});

export const Board = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '24px',
  width: '100%',
  minWidth: 0,

  [forMobile(muiTheme)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '16px',
  },
}));

export const Panel = styled(Stack)({
  flexDirection: 'column',
  gap: '16px',
  padding: '20px',
  borderRadius: theme.radius[16],
  backgroundColor: theme.colors.background.canvas,
  border: `1px solid ${theme.colors.border.subtle}`,
  boxSizing: 'border-box',
});

export const SidePanel = styled(Panel)(({ theme: muiTheme }) => ({
  width: '280px',
  flexShrink: 0,

  [forMobile(muiTheme)]: {
    width: '100%',
  },
}));

export const MainPanel = styled(Panel)(({ theme: muiTheme }) => ({
  flex: 1,
  minWidth: 0,

  [forMobile(muiTheme)]: {
    width: '100%',
  },
}));

export const PanelHeader = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',

  [forMobile(muiTheme)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const PanelHeaderTitle = styled(Stack)({
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
});

export const BackRow = styled(Stack)({
  flexDirection: 'row',
  alignSelf: 'flex-start',
});

export const PanelTitleRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
});

export const BoardToolbar = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: theme.spacing[16],

  [forMobile(muiTheme)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const PanelHeaderActions = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
  flexWrap: 'wrap',
});

export const RoleGroup = styled(Stack)({
  flexDirection: 'column',
  gap: '4px',
});

export const RoleScroller = styled(Stack)({
  flexDirection: 'column',
  gap: '16px',
  // Tall enough for the seeded roles plus a few custom ones before it scrolls.
  maxHeight: 'max(520px, calc(100vh - 320px))',
  overflowY: 'auto',
  paddingRight: '4px',
  ...scrollbarStyles,
});

export const RoleItem = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>(({ $active }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  padding: '10px 12px',
  border: `1px solid ${$active ? theme.colors.border.default : 'transparent'}`,
  borderRadius: theme.radius[8],
  backgroundColor: $active ? theme.colors.background.raised : 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',

  '&:hover': {
    backgroundColor: theme.colors.background.raised,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.colors.border.focus}`,
    outlineOffset: '2px',
  },
}));

export const RoleGroupHeader = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  padding: `0 ${theme.spacing[12]}px`,
});

export const RoleItemText = styled(Stack)({
  flexDirection: 'column',
  gap: theme.spacing[2],
  minWidth: 0,
});

export const RoleItemLabel = styled(Stack)({
  flexWrap: 'wrap',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,

  '& svg': {
    width: '14px',
    height: '14px',
    flexShrink: 0,
  },
});

/**
 * Spacing for a tab's content. The design-system `Tabs.Panel` is deliberately
 * unstyled (`styled(Tabs.Panel)({})`), so without this the panel's first element
 * sits flush against the tab bar's baseline.
 *
 * The gap separates the *blocks* inside a tab — a toolbar, a notice, the list —
 * never the rows of a list, which stay adjacent so `MemberRow`'s hairline
 * separators read as one table. Hence `MemberList` below rather than letting
 * this gap apply to the rows directly.
 */
export const TabPanelContent = styled(Stack)({
  flexDirection: 'column',
  gap: '16px',
  paddingTop: '16px',
});

export const MemberList = styled(Stack)({
  flexDirection: 'column',
});

export const MemberRow = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '12px',
  borderRadius: theme.radius[8],

  '& + &': {
    borderTop: `1px solid ${theme.colors.border.subtle}`,
  },

  '&:hover': {
    backgroundColor: theme.colors.background.raised,
  },

  [forMobile(muiTheme)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '8px',
  },
}));

export const MemberRowMain = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[12],
  minWidth: 0,
});

export const MemberIdentityRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const MemberToolbar = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[12],

  [forMobile(muiTheme)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const MemberIdentity = styled(Stack)({
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
});

export const MemberActions = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
  flexWrap: 'wrap',
});

export const CandidateList = styled(Stack)({
  flexDirection: 'column',
  gap: '4px',
  maxHeight: '320px',
  overflowY: 'auto',
  ...scrollbarStyles,
});

export const CandidateRow = styled('label')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 12px',
  borderRadius: theme.radius[8],
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: theme.colors.background.raised,
  },
});

export const ModalBody = styled(Stack)({
  flexDirection: 'column',
  gap: '16px',
  minWidth: '320px',
});

export const SideColumn = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'column',
  gap: '16px',
  width: '280px',
  flexShrink: 0,

  [forMobile(muiTheme)]: {
    width: '100%',
  },
}));

export const CenteredState = styled(Stack)({
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '40px 16px',
  textAlign: 'center',
});
